import { useBookSeries } from "@/lib/hooks/bookLessons/useBookLessons";

export interface BookScope {
  seriesSlug: string | null;
  levelId: string | null;
  componentId: string | null;
}

export const EMPTY_BOOK_SCOPE: BookScope = {
  seriesSlug: null,
  levelId: null,
  componentId: null,
};

const PLACEHOLDER = "";

interface Option {
  value: string;
  label: string;
}

export function Field({
  id,
  label,
  value,
  placeholder,
  disabled,
  options,
  onChange,
}: {
  id: string;
  label: string;
  value: string | null;
  placeholder: string;
  disabled: boolean;
  options: Option[];
  onChange: (value: string | null) => void;
}) {
  return (
    <label htmlFor={id} className="flex flex-col gap-1">
      <span className="text-[11px] font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400">
        {label}
      </span>
      <select
        id={id}
        value={value ?? PLACEHOLDER}
        disabled={disabled}
        onChange={(event) =>
          onChange(event.target.value === PLACEHOLDER ? null : event.target.value)
        }
        className="rounded-lg border border-gray-200 bg-background px-2.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/40 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-800"
      >
        <option value={PLACEHOLDER}>{placeholder}</option>
        {options.map((one) => (
          <option key={one.value} value={one.value}>
            {one.label}
          </option>
        ))}
      </select>
    </label>
  );
}

export function BookScopePicker({
  idPrefix,
  value,
  onChange,
}: {
  idPrefix: string;
  value: BookScope;
  onChange: (value: BookScope) => void;
}) {
  const { series, loading } = useBookSeries();
  const selectedSeries = series.find((one) => one.slug === value.seriesSlug);

  return (
    <>
      <Field
        id={`${idPrefix}-series`}
        label="Series"
        value={value.seriesSlug}
        placeholder={loading ? "Loading…" : "Select"}
        disabled={loading}
        options={series.map((one) => ({ value: one.slug, label: one.slug }))}
        onChange={(seriesSlug) => onChange({ ...EMPTY_BOOK_SCOPE, seriesSlug })}
      />
      <Field
        id={`${idPrefix}-level`}
        label="Level"
        value={value.levelId}
        placeholder="Select"
        disabled={!selectedSeries}
        options={(selectedSeries?.levels ?? []).map((one) => ({ value: one.id, label: one.name }))}
        onChange={(levelId) => onChange({ ...value, levelId })}
      />
      <Field
        id={`${idPrefix}-component`}
        label="Component"
        value={value.componentId}
        placeholder="Select"
        disabled={!selectedSeries}
        options={(selectedSeries?.components ?? []).map((one) => ({
          value: one.id,
          label: one.name,
        }))}
        onChange={(componentId) => onChange({ ...value, componentId })}
      />
    </>
  );
}
